import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Icon } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { saveBlob } from '../file-helpers';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ AN ADDRESS FOR A FILE, SO ONE CAN BE LINKED TO FROM WRITTEN TEXT.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Asked for on 15 Sep: dropping a file on a task's description must put the file **in the
 * description**. That needs a link in stored HTML, and a link needs an address — which this product
 * did not have. Every file is read through `session.request`, an authenticated `fetch` that hands
 * back bytes; `downloadAttachment` turns those into a blob URL and clicks it. Nothing there is a
 * URL anybody can write down.
 *
 * ⚠️⚠️ **THE TWO OBVIOUS ANSWERS ARE BOTH WRONG, AND THIS IS THE THIRD.**
 *
 *  1. **Put the token in the URL** — `?token=…` or a signed `GET /files/:id`. That is a capability
 *     URL: whoever holds the string reads the file, for as long as it lives, with no session and no
 *     permission check. It leaks through referrers, proxy logs, chat previews and shoulder. For a
 *     link that is *stored in a task description for years*, a short expiry breaks it and a long one
 *     is a permanent grant.
 *  2. **Let the browser send a cookie** — the access token is a Bearer header, not a cookie, and the
 *     refresh cookie is scoped to `/auth` precisely so it cannot be replayed against data routes.
 *     Changing that to make an `<img>` work would be re-architecting authentication for a link.
 *
 * ⚠️⚠️ **SO THE LINK POINTS AT THE APP, NOT AT THE API.** `/{workspace}/files/{id}` is an ordinary
 * screen: the session is already there, the permission check is the API's as usual, and the route's
 * only job is to fetch the bytes and hand them over. Pasted into a chat it opens the app and signs
 * the reader in first; opened in a new tab it works; opened by somebody without `task.view` it
 * refuses exactly as the API does. No new grant of any kind exists.
 *
 * ⚠️ **It downloads and then LEAVES**, replacing itself in history, so the back button does not
 * bounce a reader between the file and wherever they came from.
 *
 * ⚠️ **What it is NOT: an `<img src>`.** An image tag pointed here would load this HTML, not the
 * picture — so an inline preview still needs the addressable-file work above, with the security
 * decision that goes with it. A dropped image lands as a named link like everything else.
 */
export function FileRoute() {
  const t = useT();
  const session = useSession();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<'fetching' | 'done' | 'gone' | 'failed'>('fetching');

  useEffect(() => {
    if (id === undefined) return;

    void (async () => {
      /**
       * ⚠️⚠️ **ONE REQUEST, AND THE NAME COMES OUT OF ITS HEADER.** The obvious shape is a metadata
       * call for the filename and then a second call for the bytes — two round trips, and a
       * `/files/:id/meta` endpoint invented for a screen. `GET /files/:id` already sets
       * `content-disposition` with the stored name (RFC 6266, both spellings), which is the same
       * name the row would have given and is correct after a rename.
       *
       * ⚠️ **The name is DATA that came from an uploader**, so it is never treated as a path — the
       * fallback is a flat string and the browser writes it into the downloads folder as a name.
       */
      const response = await session.request(`/files/${id}`).catch(() => null);
      if (response?.ok !== true) {
        /* ⚠️ **404 IS THE COMMON CASE AND IT IS NOT AN ERROR** — 15 Sep, from the first report of
           this feature. A link written into a description outlives the file it names: delete the
           attachment and the text still points at it. *Not available* reads as a fault in the
           product; *this file has been deleted* reads as what happened. */
        setState(response?.status === 404 ? 'gone' : 'failed');
        return;
      }

      saveBlob(await response.blob(), filenameFrom(response.headers.get('content-disposition')));

      /**
       * ⚠️⚠️ **THERE IS OFTEN NOWHERE TO GO BACK TO, BECAUSE THE LINK OPENS A NEW TAB.** The
       * sanitiser adds `target="_blank" rel="noopener noreferrer"` to every stored link — correct,
       * and it means this screen is frequently the FIRST entry in its tab's history. `navigate(-1)`
       * there does nothing at all, so the screen would sit on *Downloading…* for ever after the
       * file had already been saved. Measured against the running API, not assumed.
       *
       * ⚠️ `history.length > 1` is the only test a page gets, and it is approximate — a tab that
       * was navigated before landing here counts. Approximate is fine: the worst case is a *saved*
       * message on a tab that could have gone back, which is a message rather than a dead end.
       */
      if (window.history.length > 1) void navigate(-1);
      else setState('done');
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object each
       render; listing it re-downloads for ever (the same trap `attachments.tsx` records). */
  }, [id]);

  return (
    <div data-screen="file" className="flex min-h-0 flex-1 items-center justify-center p-8">
      {state === 'done' ? (
        <div className="flex flex-col items-center gap-2 text-center">
          <Icon name="check" size={24} className="text-muted-foreground" />
          <p className="text-[13px]">{t('file.saved')}</p>
        </div>
      ) : state === 'gone' || state === 'failed' ? (
        <div className="flex flex-col items-center gap-3 text-center">
          <Icon name="file-text" size={24} className="text-muted-foreground" />
          <p className="text-[13px]">
            {t(state === 'gone' ? 'file.deleted' : 'file.notAvailable')}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void navigate(-1);
            }}
          >
            {t('common.back')}
          </Button>
        </div>
      ) : (
        <p className="text-muted-foreground text-[13px]">{t('file.fetching')}</p>
      )}
    </div>
  );
}

/**
 * The filename out of a `content-disposition`, or a plain fallback.
 *
 * ⚠️ **`filename*` first**, because that is the UTF-8 one: the plain `filename` beside it is an
 * ASCII fallback with every accent stripped, so preferring it would save *Szerzodes.pdf* for a file
 * called *Szerződés.pdf*. `files.controller.ts` writes both and says why.
 */
function filenameFrom(header: string | null): string {
  const utf8 = /filename\*=UTF-8''([^;]+)/iu.exec(header ?? '');
  if (utf8?.[1] !== undefined) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* A malformed escape is not a reason to fail the download — fall through to the ASCII name. */
    }
  }
  const ascii = /filename="([^"]*)"/iu.exec(header ?? '');
  return ascii?.[1] !== undefined && ascii[1] !== '' ? ascii[1] : 'file';
}
